<!--
  ReportSectionTable.vue  —  Engine E section renderer for server type "table".
  Purpose: render a report table section as a CSS-grid list. Columns carry
    {field,title,width,highlight_min_max}; data is one-or-more sets of {rows[]}
    unioned + grouped; each rendered cell is {v, raw, isMin, isMax}; per-column
    min/max highlighting is applied for columns flagged highlight_min_max.

  reconstructed from lacak.io deployed behavior; app-specific contract verbatim,
  internal impl re-authored.

  VERBATIM (1f59.js:104-158):
    - component name ......... "report-section-table"
    - column fields .......... field, title, width, highlight_min_max
    - data shape ............. section.data = [ { rows: [ { <field>: value } ] } ]
    - cell model ............. { v, raw, isMin, isMax }
    - unionDataSets .......... flatten data[].rows, tag groupIndex + id "{grp}_{row}"
    - applyMinMax ............ per column with highlight_min_max, flag min/max cell
    - grid ................... CSS var --colTemplate = <width>fr per column
-->
<template>
  <v-card class="report-section-table">
    <v-card-title v-if="section.header" class="report-section-table__header">
      {{ section.header }}
    </v-card-title>

    <div class="report-section-table__grid" :style="gridStyle">
      <!-- header row -->
      <div
        v-for="(col, ci) in columns"
        :key="'h-' + ci"
        class="report-section-table__cell report-section-table__cell--head"
      >
        {{ col.title }}
      </div>

      <!-- body rows (union of all data sets) -->
      <template v-for="row in rows">
        <div
          v-for="(cell, ci) in row.cells"
          :key="row.id + '-' + ci"
          class="report-section-table__cell"
          :class="{
            'report-section-table__cell--min': cell.isMin,
            'report-section-table__cell--max': cell.isMax,
          }"
        >
          {{ cell.v }}
        </div>
      </template>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'report-section-table',

  props: {
    section: { type: Object, required: true },
  },

  computed: {
    columns() {
      return this.section.columns || [];
    },

    // CSS grid template: one <width>fr track per column (default 1fr).
    // Exposed as the recovered custom property --colTemplate.
    colTemplate() {
      return this.columns
        .map((c) => (c.width ? c.width : 1) + 'fr')
        .join(' ');
    },

    gridStyle() {
      return {
        '--colTemplate': this.colTemplate,
        gridTemplateColumns: 'var(--colTemplate)',
      };
    },

    // unionDataSets: flatten every data set's rows into a single list, tagging
    // each with its source set (groupIndex) and a synthetic id "{grp}_{row}".
    unionRows() {
      const data = this.section.data || [];
      const out = [];
      data.forEach((set, grp) => {
        const rows = (set && set.rows) || [];
        rows.forEach((row, r) => {
          out.push({ id: grp + '_' + r, groupIndex: grp, row });
        });
      });
      return out;
    },

    // applyMinMax: per column flagged highlight_min_max, compute the numeric
    // min & max across all unioned rows.
    minMaxByField() {
      const acc = {};
      this.columns.forEach((col) => {
        if (!col.highlight_min_max) return;
        let min = Infinity;
        let max = -Infinity;
        let seen = false;
        this.unionRows.forEach(({ row }) => {
          const raw = row ? row[col.field] : undefined;
          const n = Number(raw);
          if (raw !== undefined && raw !== null && raw !== '' && !isNaN(n)) {
            seen = true;
            if (n < min) min = n;
            if (n > max) max = n;
          }
        });
        if (seen) acc[col.field] = { min, max };
      });
      return acc;
    },

    // Materialize each row into an array of {v, raw, isMin, isMax} cells.
    rows() {
      return this.unionRows.map(({ id, groupIndex, row }) => ({
        id,
        groupIndex,
        cells: this.columns.map((col) => {
          const raw = row ? row[col.field] : undefined;
          const mm = this.minMaxByField[col.field];
          const n = Number(raw);
          const numeric =
            raw !== undefined && raw !== null && raw !== '' && !isNaN(n);
          return {
            v: raw === undefined || raw === null ? '' : raw,
            raw,
            isMin: !!(mm && numeric && n === mm.min),
            isMax: !!(mm && numeric && n === mm.max),
          };
        }),
      }));
    },
  },
};
</script>

<style scoped>
.report-section-table__grid {
  display: grid;
  grid-template-columns: var(--colTemplate);
  width: 100%;
  overflow-x: auto;
}
.report-section-table__cell {
  padding: 6px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.report-section-table__cell--head {
  font-weight: 600;
  border-bottom: 2px solid rgba(0, 0, 0, 0.2);
}
.report-section-table__cell--min {
  color: #c62828;
  font-weight: 600;
}
.report-section-table__cell--max {
  color: #2e7d32;
  font-weight: 600;
}
</style>
